import React from 'react'
import { Button, List, Space } from 'antd'
import PropTypes from 'prop-types'
import { observer } from 'mobx-react-lite'
import { DeleteOutlined } from '@ant-design/icons'
import ProgressInput from '../ProgressInput'
import HistoryView from './HistoryView'
import { FormatedTimestamp, StatusTag } from '../../Shared'
import { useSmartTranslation } from '../../../cls/TranslationContext'
/**
 * SubtasksList renders a list of subtasks for an item, with controls for progress,
 * resetting history, and viewing detailed history.
 *
 * @component
 * @param {Object} props
 * @param {Object} props._itm            The item containing subtasks and recurrence info.
 * @param {string} [props.historyKey]     Key to identify the history drawer modal.
 * @param {string} [props.category]       History category (e.g., 'subtasks' or 'recurrence_subtasks').
 * @param {Function} props.getHistory     Function to retrieve full history for a given subtask ID.
 * @param {Object} props.uiStore          MobX store for UI state (modal opening, etc.).
 * @param {Array} props.dataSource        Array of subtask objects to display.
 * @param {Function} props._doSave        Handler to save subtask progress or reset history.
 * @param {Function} props.renderName     Function to render the display name of a subtask.
 *
 * @returns {JSX.Element} The rendered list of subtasks with controls.
 */
export const SubtasksList = observer(
	({
		_itm,
		historyKey = 'history-drawer',
		category = 'subtasks',
		getHistory,
		uiStore,
		dataSource,
		_doSave,
		renderName
	}) => {
		const { t } = useSmartTranslation()
		const recurrenceNext = _itm.recurrence?.next || false
		return (
			<>
				<p id="track-no-sub-no-rec">
					{_itm.displayName} -- {t('recurrence.' + category)}
				</p>
				<Space style={{ width: '100%' }}>
					{recurrenceNext && (
						<p>
							{t('single.next')}
							{': '}
							<FormatedTimestamp timestamp={recurrenceNext} />
						</p>
					)}
					<Button
						className="no-print"
						type="link"
						onClick={() => {
							_doSave(null, 'reset-history')
						}}
						icon={<DeleteOutlined />}
					>
						{t('__history%single.reset')}
					</Button>
				</Space>

				{dataSource.length > 0 && (
					<List
						bordered
						dataSource={dataSource}
						renderItem={(subtask) => (
							<List.Item
								key={subtask.id}
								actions={[
									_itm.progress === 'completed' ? (
										<StatusTag
											tag="completed"
											style={{ marginRight: 3 }}
										/>
									) : (
										<ProgressInput
											key={subtask.id + 'status'}
											value={subtask.status}
											onChange={(value) => {
												_doSave(subtask.id, value)
											}}
										/>
									),
									<HistoryView
										modalKey={historyKey}
										key={subtask.id + 'history'}
										_itm={subtask}
										uiStore={uiStore}
										category={category}
										getHistory={getHistory}
									/>
								]}
							>
								{renderName(subtask.id)}
							</List.Item>
						)}
					/>
				)}
			</>
		)
	}
)
SubtasksList.propTypes = {
	getHistory: PropTypes.func,
	dataSource: PropTypes.array.isRequired,
	_doSave: PropTypes.func.isRequired
}
